import { Component, effect, inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { DialogShellComponent } from '@custom-components/overlay/dialog-shell/dialog-shell.component';
import { MatRadioModule } from '@angular/material/radio';
import { SpinnerComponent } from '@custom-components/general/spinner/spinner.component';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import {
  Category,
  DeviceChannel,
  DeviceConfigurationForDeviceChannel,
  DeviceInterfaceContext,
  DeviceConfigurationUpdate,
  DeviceInterface,
  DynamicParameterWithValue,
  Subdevice,
} from '@core/models';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { DeviceStore } from '@root/app/store/device.store';
import { DeviceService } from '@core/services';
import { concatMap, first, interval, switchMap } from 'rxjs';
import {
  FilledButtonComponent,
  TextButtonComponent,
  OutlinedButtonComponent,
} from '@custom-components/general/button';

@Component({
  selector: 'xemex-device-configuration-details-dialog',
  imports: [
    MatIconModule,
    MatDialogModule,
    MatRadioModule,
    SpinnerComponent,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    TranslateModule,
    DialogShellComponent,
    FilledButtonComponent,
    TextButtonComponent,
    OutlinedButtonComponent,
  ],
  providers: [TranslatePipe],
  templateUrl: './device-configuration-details-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './device-configuration-details-dialog.component.scss',
})
export class DeviceConfigurationDetailsDialogComponent implements OnDestroy {
  readonly deviceStore = inject(DeviceStore);
  readonly dialogRef = inject(MatDialogRef<DeviceConfigurationDetailsDialogComponent>);
  public data = inject(MAT_DIALOG_DATA);
  private readonly deviceService = inject(DeviceService);

  channel: DeviceChannel;
  protocol: string;
  // 'add' = creating a new sub device, 'edit' = changing an existing channel.
  mode: 'add' | 'edit';
  categoryId: number;
  categories: Category[] | undefined;
  channelNumber: number;
  configurationFileId?: number;
  description?: string;
  deviceConfigurations: DeviceConfigurationForDeviceChannel[] = [];
  interfaces: DeviceInterface[];
  subDevices: Subdevice[];
  dynamicParameters: DynamicParameterWithValue[];
  isLoadingDeviceConfigurations = false;
  isSending = false;
  sendError: string | null = null;
  succeeded = false;
  countdown = 3;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly translateService: TranslatePipe) {
    this.channel = this.data.channel;
    this.protocol = this.data.protocol;
    this.mode = this.data.mode ?? 'edit';
    this.description = this.channel.deviceConfiguration?.description;
    this.configurationFileId = this.channel.deviceConfiguration?.fileId;
    this.channelNumber = this.channel.channelNumber;
    this.categoryId = this.channel.categoryId ?? 1;
    this.subDevices = this.channel.deviceConfiguration?.devices ?? [];
    this.dynamicParameters = this.channel.deviceConfiguration?.dynamicParameters ?? [];
    this.interfaces = [];
    effect(() => {
      this.categories = this.deviceStore.categories();
    });
    effect(() => {
      this.deviceConfigurations = this.deviceStore.deviceConfigurations();
      this.isLoadingDeviceConfigurations = this.deviceStore.isLoadingDeviceConfigurations();
      this.onDeviceConfigurationFileIdChange();
    });
    if (this.protocol === 'DLMS') {
      this.deviceStore.getDeviceConfigurationsForChannelNumber(this.channel.channelNumber);
    } else {
      this.deviceStore.getDeviceConfigurationsForCategoryMethod(this.categoryId);
      if (this.deviceStore.categories() === undefined) {
        this.deviceStore.getCategoriesMethod();
      }
    }
  }

  getAllSubdeviceNames(): string[] {
    return [
      ...new Set(
        this.subDevices
          .map(s => s.deviceName)
          .concat(this.dynamicParameters.map(d => d.deviceName)),
      ),
    ];
  }

  isValid(): boolean {
    return (
      !!this.configurationFileId &&
      this.dynamicParameters.every(d => d.value != null && d.value != '')
    );
  }

  onDeviceConfigurationFileIdChange(): void {
    const selectedConfigurationFile = this.deviceConfigurations.find(
      (d: DeviceConfigurationForDeviceChannel) => d.fileId == this.configurationFileId,
    );

    if (selectedConfigurationFile) {
      this.subDevices = selectedConfigurationFile.devices;
      this.description = selectedConfigurationFile.description;
      this.dynamicParameters = selectedConfigurationFile.dynamicParameters.map(d => {
        return { ...d, value: '' };
      });
      this.interfaces = selectedConfigurationFile.interfaces ?? [];
    } else {
      this.description = undefined;
      this.subDevices = [];
      this.dynamicParameters = [];
      this.interfaces = [];
    }
    if (this.configurationFileId == this.data.channel.deviceConfiguration?.fileId) {
      this.subDevices = this.channel.deviceConfiguration?.devices ?? [];
      this.dynamicParameters = this.channel.deviceConfiguration?.dynamicParameters ?? [];
    }
  }

  onCategoryChange(change: MatSelectChange): void {
    const newCategoryId = change.value;
    this.deviceStore.getDeviceConfigurationsForCategoryMethod(newCategoryId);
    this.configurationFileId = undefined;
    this.description = undefined;
    this.subDevices = [];
    this.dynamicParameters = [];
  }

  ngOnDestroy(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  }

  onChange(change: string) {
    if (change == 'deviceConfig') {
      if (this.configurationFileId == null) {
        console.error('configurationFileId may not be null');
        return;
      }

      this.isSending = true;
      this.sendError = null;

      const deviceId = this.deviceStore.deviceDetail()!.deviceId;
      const context = this.constructContext();
      const deviceConfigurationUpdate: DeviceConfigurationUpdate = {
        channelNumber: this.channelNumber,
        context,
        dynamicParameters: this.dynamicParameters,
        interfaceIds: this.interfaces.map(i => i.interfaceId),
        fileId: this.configurationFileId,
      };

      this.deviceService
        .sendDeviceConfigurationFileToDevice(deviceId, deviceConfigurationUpdate)
        .pipe(
          switchMap(({ deviceConfigDispatchId }) =>
            interval(1000).pipe(
              concatMap(() =>
                this.deviceService.sendDeviceConfigurationDispatch(deviceConfigDispatchId),
              ),
              first(data => !['Pending', 'In Progress'].includes(data.status)),
            ),
          ),
        )
        .subscribe({
          next: data => {
            this.isSending = false;
            this.succeeded = data.status == 'Success';
            this.sendError = data.error;
            if (this.succeeded) {
              this.deviceStore.getChannelConfigurationMethod(deviceId);
              this.startCountdown();
            }
          },
          error: err => {
            this.isSending = false;
            this.sendError = err?.error?.message ?? err?.message ?? 'An unexpected error occurred';
          },
        });
    } else {
      this.dialogRef.close(null);
    }
  }

  private startCountdown(): void {
    this.countdown = 3;
    this.countdownTimer = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        clearInterval(this.countdownTimer!);
        this.countdownTimer = null;
        this.dialogRef.close(null);
      }
    }, 1000);
  }

  private constructContext(): Record<string, Record<string, DeviceInterfaceContext>> {
    let context: Record<string, Record<string, DeviceInterfaceContext>> = {};
    for (let subDevice of this.subDevices) {
      let subDeviceContext: Record<string, DeviceInterfaceContext> = {};
      let emeterInterfacesWithoutTarget = subDevice.interfaces.filter(
        i => i.name == 'emeter' && i.context?.target == null,
      );
      for (let subDeviceInterface of emeterInterfacesWithoutTarget) {
        subDeviceContext[subDeviceInterface.name] = subDeviceInterface.context;
      }
      context[subDevice.deviceName] = subDeviceContext;
    }
    return context;
  }

  translateParameterName(parameterName: string): string {
    const prefix = 'devices.detailTabs.configuration.parameterNames.';
    switch (parameterName) {
      case '$modbusRtuAddress$':
        return this.translateService.transform(prefix + 'modbusRtuAddress');
      case '$modbusTcpAddress$':
        return this.translateService.transform(prefix + 'modbusTcpAddress');
      case '$modbusTcpAddressAsString$':
        return this.translateService.transform(prefix + 'modbusTcpAddressAsString');
      case '$modbusTcpPort$':
        return this.translateService.transform(prefix + 'modbusTcpPort');
      default:
        return parameterName.replaceAll('$', '');
    }
  }
}
